<template>
  <Teleport to="body">
    <transition name="bm-fade">
      <div
        v-if="open"
        ref="backdropRef"
        class="modal-backdrop bm-backdrop"
        :class="backdropClass"
        :style="backdropStyle"
        role="presentation"
        @click="onBackdropClick"
        @keydown.esc.stop.prevent="onEsc"
        tabindex="-1"
      >
        <div
          ref="cardRef"
          class="modal-content bm-card"
          :class="cardClass"
          :style="cardStyle"
          role="dialog"
          aria-modal="true"
          :aria-label="ariaLabel || undefined"
        >
          <button
            v-if="!hideClose"
            type="button"
            class="modal-close-corner bm-close"
            :aria-label="$t('common.close')"
            @click="emitClose('button')"
          >×</button>
          <slot />
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
// Reusable modal frame with the FS2/Studio purple-glass recipe + UX
// hardening:
//
//   • ESC key closes (priority stack so only the topmost modal handles ESC)
//   • body scroll locked while open
//   • autofocus first focusable element on open (or el matching `autofocus`
//     prop selector)
//   • click-outside dismisses by default; pass `dismiss-guard` to require
//     an explicit close (used for type-to-confirm destructive flows)
//
// Slot the modal body inside; the close × button is rendered automatically.

let modalStack = [];

function getScrollbarWidth() {
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
}

export default {
  name: 'BaseModal',
  props: {
    open: { type: Boolean, default: false },
    ariaLabel: { type: String, default: '' },
    hideClose: { type: Boolean, default: false },
    /** When true, click on backdrop or ESC will NOT close. Used for
     *  destructive type-to-confirm flows where accidental dismissal is bad. */
    dismissGuard: { type: Boolean, default: false },
    /** When true, ESC closes regardless of dismissGuard (rare). */
    escAlwaysCloses: { type: Boolean, default: false },
    /** Optional CSS class overrides */
    backdropClass: { type: [String, Array, Object], default: '' },
    cardClass: { type: [String, Array, Object], default: '' },
    backdropStyle: { type: [String, Object], default: '' },
    cardStyle: { type: [String, Object], default: '' },
    /** CSS selector for the element to focus on open. Defaults to first
     *  focusable inside the card. */
    autofocus: { type: String, default: '' },
  },
  emits: ['close', 'update:open'],
  data() {
    return {
      _prevBodyOverflow: '',
      _prevBodyPaddingRight: '',
      _prevActive: null,
    };
  },
  watch: {
    open(v) {
      if (v) this.$nextTick(this._onOpen);
      else this._onClose();
    },
  },
  mounted() {
    if (this.open) this.$nextTick(this._onOpen);
  },
  beforeUnmount() {
    if (modalStack.includes(this)) this._onClose();
  },
  methods: {
    onEsc() {
      if (this.dismissGuard && !this.escAlwaysCloses) return;
      // Only the topmost modal in the stack reacts.
      if (modalStack[modalStack.length - 1] !== this) return;
      this.emitClose('esc');
    },
    onBackdropClick(e) {
      if (e.target !== this.$refs.backdropRef) return;
      if (this.dismissGuard) return;
      this.emitClose('backdrop');
    },
    emitClose(reason) {
      this.$emit('update:open', false);
      this.$emit('close', reason);
    },
    _onOpen() {
      // Lock body scroll
      const sbw = getScrollbarWidth();
      this._prevBodyOverflow = document.body.style.overflow;
      this._prevBodyPaddingRight = document.body.style.paddingRight;
      document.body.style.overflow = 'hidden';
      if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;

      // Stack tracking so ESC only fires on top
      if (!modalStack.includes(this)) modalStack.push(this);

      // Save previously-focused element to restore on close
      this._prevActive = document.activeElement;

      // Move focus
      const card = this.$refs.cardRef;
      if (!card) return;
      let target = null;
      if (this.autofocus) {
        target = card.querySelector(this.autofocus);
      }
      if (!target) {
        target = card.querySelector(
          'input:not([type="hidden"]):not([disabled]),' +
          'textarea:not([disabled]),' +
          'select:not([disabled]),' +
          'button:not([disabled]):not(.bm-close),' +
          '[tabindex]:not([tabindex="-1"])'
        );
      }
      if (!target) target = card;
      try { target.focus({ preventScroll: true }); } catch (_) {}
    },
    _onClose() {
      modalStack = modalStack.filter(m => m !== this);
      if (modalStack.length === 0) {
        document.body.style.overflow = this._prevBodyOverflow || '';
        document.body.style.paddingRight = this._prevBodyPaddingRight || '';
      }
      // Restore focus
      try { this._prevActive && this._prevActive.focus({ preventScroll: true }); } catch (_) {}
      this._prevActive = null;
    },
  },
};
</script>

<style scoped>
.modal-backdrop.bm-backdrop {
  position: fixed; inset: 0; z-index: 9000;
  background: rgba(0, 0, 0, 0.7);
  display: flex; align-items: center; justify-content: center;
  padding: calc(var(--site-nav-height, 96px) + 12px)
           16px
           max(env(safe-area-inset-bottom, 16px), 16px);
  box-sizing: border-box;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  outline: none;
}

.modal-content.bm-card {
  position: relative;
  width: min(560px, 92vw);
  max-height: calc(100vh - var(--site-nav-height, 96px) - 32px);
  background: linear-gradient(
    180deg,
    rgba(40, 24, 64, 0.92) 0%,
    rgba(28, 18, 48, 0.88) 100%
  );
  border: 1px solid rgba(192, 38, 211, 0.30);
  border-radius: 14px;
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset,
    0 0 24px rgba(124, 58, 237, 0.18);
  display: flex; flex-direction: column;
  overflow: hidden;
  color: #f4f1ff;
}

.modal-content.bm-card::before {
  content: '';
  position: absolute;
  left: 50%; top: 0;
  width: 60%; height: 1px;
  transform: translateX(-50%);
  background-image: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0)    2.56%,
    rgba(255, 255, 255, 0.45) 51.48%,
    rgba(255, 255, 255, 0)   97.44%
  );
  pointer-events: none;
  z-index: 1;
}

.modal-close-corner.bm-close {
  position: absolute; top: 10px; right: 10px;
  width: 32px; height: 32px; border-radius: 50%;
  background: rgba(0, 0, 0, 0.55); color: #fff; border: none;
  font-size: 20px; line-height: 1; cursor: pointer; z-index: 2;
}
.modal-close-corner.bm-close:hover { background: rgba(0, 0, 0, 0.75); }

.bm-fade-enter-active, .bm-fade-leave-active {
  transition: opacity 0.18s ease;
}
.bm-fade-enter-from, .bm-fade-leave-to { opacity: 0; }
</style>
